import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import StatisticsService from "../../../services/statistics.service";
import StatDescriptionTooltip from "./StatDescriptionTooltip";
import StatAsyncContent from "./StatAsyncContent";
import StatLineChart from "./StatLineChart";
import StatSelect from "./StatSelect";
import { getProductStroke, TOTAL_STROKE } from "./statChartTheme";

const MONTH_LABELS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function buildMonthChartData(totalsByMonth, productSeriesNested) {
  const byMonth = {};
  for (let i = 1; i <= 12; i++) {
    byMonth[i] = { month: MONTH_LABELS[i - 1], total: 0 };
  }

  for (const item of totalsByMonth ?? []) {
    if (!byMonth[item.month_number]) continue;
    byMonth[item.month_number].total = item.total_users_with_active_membership ?? 0;
  }

  const seriesNames = [];
  const seen = new Set();

  for (const point of (productSeriesNested ?? []).flat()) {
    const name = point.product_name || "Sin producto";
    if (!seen.has(name)) {
      seen.add(name);
      seriesNames.push(name);
    }
    const row = byMonth[point.month_number];
    if (!row) continue;
    row[name] = (row[name] ?? 0) + (point.total_users ?? 0);
  }

  for (const row of Object.values(byMonth)) {
    for (const name of seriesNames) {
      if (row[name] == null) row[name] = 0;
    }
  }

  return {
    data: Array.from({ length: 12 }, (_, i) => byMonth[i + 1]),
    seriesNames,
  };
}

export default function ActiveMembershipsByMonthStat() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [data, setData] = useState(null);
  const [lines, setLines] = useState(null);
  const [description, setDescription] = useState(null);
  const [error, setError] = useState(null);
  const [loadingYears, setLoadingYears] = useState(true);

  useEffect(() => {
    StatisticsService.getMembershipManagedYears().then(({ data, error }) => {
      setLoadingYears(false);
      if (error) {
        setError(error);
        return;
      }
      const yearList = (data ?? []).map((item) => item.year);
      setYears(yearList);
      if (yearList.length > 0) setSelectedYear(yearList[0]);
    });
  }, []);

  useEffect(() => {
    if (!selectedYear) return;

    setData(null);
    setLines(null);
    setError(null);

    Promise.all([
      StatisticsService.getActiveMembershipsByMonth(selectedYear),
      StatisticsService.getActiveMembershipsByProduct(selectedYear),
    ]).then(([totalsResult, productsResult]) => {
      if (totalsResult.error) {
        setError(totalsResult.error);
        return;
      }
      if (productsResult.error) {
        setError(productsResult.error);
        return;
      }

      const { data: chartData, seriesNames } = buildMonthChartData(
        totalsResult.data,
        productsResult.data
      );

      setData(chartData);
      setLines([
        { dataKey: "total", name: "Total", stroke: TOTAL_STROKE },
        ...seriesNames.map((name, index) => ({
          dataKey: name,
          name,
          stroke: getProductStroke(index, TOTAL_STROKE),
        })),
      ]);
      setDescription(productsResult.description ?? totalsResult.description);
    });
  }, [selectedYear]);

  return (
    <Card sx={{ flex: 1, minWidth: 320, overflow: "visible" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="h6">Membresías activas por mes</Typography>
            <StatDescriptionTooltip description={description} />
          </Box>

          <StatSelect
            label="Año"
            labelId="membership-year-label"
            value={selectedYear ?? ""}
            onChange={setSelectedYear}
            options={years}
            disabled={loadingYears || years.length === 0}
          />
        </Box>

        <StatAsyncContent error={error} loading={data === null}>
          <StatLineChart
            data={data}
            xKey="month"
            lines={lines}
            labelFormatter={(month) => month}
          />
        </StatAsyncContent>
      </CardContent>
    </Card>
  );
}
